import { useGetChurnCalibration } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import { ChurnCalibrationGroupDTO } from '@shared/clientTypes/DTOs/ChurnDTO';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useState } from 'react';

const percent = (part: number, total: number) => (total ? Math.round((part / total) * 100) : 0);

const GroupCard = (props: { group: ChurnCalibrationGroupDTO; accent: string }) => {
  const { group, accent } = props;
  return (
    <div className="flex flex-col gap-2 rounded-2xl bg-surfaceObject p-4">
      <span className={mergeClassNames('text-xs font-semibold uppercase tracking-wide', accent)}>
        {group.label} · {group.count}
      </span>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold tabular-nums text-white">{group.meanScore}</span>
        <span className="text-xs text-customGrey">average score</span>
      </div>
      <div className="flex gap-3 text-xs">
        <span className="text-customRed">High {percent(group.bands.high, group.count)}%</span>
        <span className="text-customGold">Medium {percent(group.bands.medium, group.count)}%</span>
        <span className="text-customGreen">Low {percent(group.bands.low, group.count)}%</span>
      </div>
    </div>
  );
};

const ChurnAccuracyPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { data, isFetching, refetch, error } = useGetChurnCalibration({ enabled: false });

  const openHandler = () => {
    setIsOpen(true);
    if (!data) refetch();
  };

  if (!isOpen) {
    return (
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={openHandler}
          className="text-xs font-semibold text-customPrimary hover:underline"
        >
          How accurate is this score?
        </button>
      </div>
    );
  }

  const churnedHigh = data ? percent(data.churned.bands.high, data.churned.count) : 0;
  const churnedMediumPlus = data
    ? percent(data.churned.bands.high + data.churned.bands.medium, data.churned.count)
    : 0;
  const activeHigh = data ? percent(data.active.bands.high, data.active.count) : 0;

  return (
    <section className="mb-6 flex flex-col gap-4 rounded-2xl bg-surfaceBackground p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-bold text-white">Accuracy check</h3>
          <p className="max-w-3xl text-xs text-customGrey">
            {data?.method ??
              'Compares the score students who later churned would have had two weeks after their last session with the scores active students have today.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data ? (
            <span className="text-[11px] text-customGrey">
              Generated {moment(data.generatedAt).fromNow()}
              {data.skippedChurned
                ? ` · ${data.skippedChurned} churned students skipped (no held session or no active enrollment at the time)`
                : ''}
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="text-xs font-semibold text-customPrimary hover:underline disabled:opacity-50"
          >
            Recompute
          </button>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="text-xs text-customGrey hover:underline"
          >
            Hide
          </button>
        </div>
      </div>

      {isFetching && !data ? (
        <div className="flex justify-center py-6">
          <LoadingSpinner className="h-5 w-5" />
        </div>
      ) : error ? (
        <p className="text-xs text-customRed">The accuracy check could not be computed.</p>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2">
            <GroupCard group={data.churned} accent="text-customRed" />
            <GroupCard group={data.active} accent="text-customGreen" />
          </div>
          <p className="text-xs text-customLight">
            {churnedHigh}% of the students who churned were already <strong>High</strong> risk two
            weeks after their last session ({churnedMediumPlus}% at least Medium), while{' '}
            {activeHigh}% of active students are High today. The bigger the gap, the better the
            score separates the two groups.
          </p>
          <div className="overflow-x-auto rounded-2xl bg-surfaceObject">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-customGrey">
                  <th className="px-4 py-2 text-left font-semibold">Signal</th>
                  <th className="px-4 py-2 text-right font-semibold">Churned</th>
                  <th className="px-4 py-2 text-right font-semibold">Active</th>
                  <th className="px-4 py-2 text-right font-semibold">Lift</th>
                </tr>
              </thead>
              <tbody>
                {data.churned.drivers.map((driver) => {
                  const active = data.active.drivers.find((item) => item.key === driver.key);
                  const activeShare = active?.share ?? 0;
                  const lift = activeShare
                    ? driver.share / activeShare
                    : driver.share
                    ? Infinity
                    : 0;
                  return (
                    <tr key={driver.key} className="border-b border-white/5 last:border-b-0">
                      <td className="px-4 py-2 text-left text-customLight">{driver.label}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{driver.share}%</td>
                      <td className="px-4 py-2 text-right tabular-nums">{activeShare}%</td>
                      <td
                        className={mergeClassNames(
                          'px-4 py-2 text-right tabular-nums',
                          lift >= 2 ? 'text-customGreen' : lift < 1 ? 'text-customGrey' : ''
                        )}
                      >
                        {lift === Infinity ? '∞' : lift ? `${lift.toFixed(1)}×` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
};

export default ChurnAccuracyPanel;
